import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSession, signOut, authClient } from '@/lib/auth-client';
import { useAuthStore } from '@/store/useAuthStore';
import { api } from '@/lib/api-client';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, User, Shield, Key, LogOut, Mail, Package, Truck, CheckCircle, Clock } from 'lucide-react';

export function AccountPage() {
  const { data: session, isLoading } = useSession();
  const { clearAuth } = useAuthStore();
  const navigate = useNavigate();
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [orders, setOrders] = useState<any[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [shipment, setShipment] = useState<any>(null);

  useEffect(() => {
    if (session?.user) {
      setOrdersLoading(true);
      api.get('/api/orders')
        .then(res => res.json())
        .then(data => {
          setOrders(data.orders || []);
        })
        .catch(() => setOrders([]))
        .finally(() => setOrdersLoading(false));
    }
  }, [session]);

  const fetchShipment = async (orderId: string) => {
    try {
      const res = await api.get(`/api/orders/${orderId}/shipment`);
      const data = await res.json();
      if (data) setShipment(data);
    } catch { setShipment(null); }
  };

  const handleViewOrder = (order: any) => {
    setSelectedOrder(order);
    if (order.status === 'shipped' || order.status === 'delivered') {
      fetchShipment(order.id);
    }
  };

  const statusSteps = [
    { status: 'pending', label: 'Order Placed', icon: Clock },
    { status: 'paid', label: 'Payment Confirmed', icon: CheckCircle },
    { status: 'shipped', label: 'Shipped', icon: Truck },
    { status: 'delivered', label: 'Delivered', icon: Package },
  ];

  const getStatusIndex = (status: string) => statusSteps.findIndex(s => s.status === status);

  const handleSignOut = async () => {
    await signOut();
    clearAuth();
    navigate('/');
    toast.success('Signed out');
  };

  const handleEnable2FA = async () => {
    if (!session?.user) return;
    setTwoFactorLoading(true);
    try {
      const { data, error } = await authClient.twoFactor.enable({
        password: currentPassword,
      });
      if (error) {
        toast.error(error.message);
      } else {
        toast.success('2FA enabled! Enter code to verify');
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    setTwoFactorLoading(true);
    try {
      const { data, error } = await authClient.twoFactor.verifyTOTP({
        code: twoFactorCode,
        trustDevice: true,
      });
      if (error) {
        toast.error(error.message);
      } else {
        toast.success('2FA verified successfully!');
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!session?.user) return;
    setTwoFactorLoading(true);
    try {
      const { data, error } = await authClient.twoFactor.disable({
        password: currentPassword,
      });
      if (error) {
        toast.error(error.message);
      } else {
        toast.success('2FA disabled');
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      const { data, error } = await authClient.changePassword({
        currentPassword,
        newPassword,
      });
      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Password changed successfully');
        setCurrentPassword('');
        setNewPassword('');
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  if (!session) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div className="flex justify-between items-center border-b pb-6">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold tracking-tight">Account Settings</h1>
            <p className="text-sm text-muted-foreground">Manage your profile, security, and preferences.</p>
          </div>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-transparent h-auto p-0 gap-4 border-b w-full justify-start rounded-none overflow-x-auto">
            <TabsTrigger value="profile" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-2 text-xs font-bold uppercase tracking-widest">
              <User className="h-4 w-4 mr-2" /> Profile
            </TabsTrigger>
            <TabsTrigger value="orders" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-2 text-xs font-bold uppercase tracking-widest">
              <Package className="h-4 w-4 mr-2" /> Orders
            </TabsTrigger>
            <TabsTrigger value="security" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-2 text-xs font-bold uppercase tracking-widest">
              <Shield className="h-4 w-4 mr-2" /> Security
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-bold uppercase">Profile Information</CardTitle>
                <CardDescription>Your account details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-2">
                  <Label className="text-xs">Email</Label>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    {session.user.email}
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label className="text-xs">User ID</Label>
                  <div className="text-sm font-mono text-muted-foreground">{session.user.id}</div>
                </div>
                {session.user.name && (
                  <div className="grid gap-2">
                    <Label className="text-xs">Name</Label>
                    <div className="text-sm">{session.user.name}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="orders" className="space-y-4">
            {ordersLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
            ) : orders.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-muted-foreground">No orders yet. <Link to="/catalog" className="text-primary hover:underline">Browse packages</Link></CardContent></Card>
            ) : selectedOrder ? (
              <div className="space-y-4">
                <Button variant="ghost" size="sm" onClick={() => setSelectedOrder(null)}>&larr; Back to orders</Button>
                <Card>
                  <CardHeader><CardTitle className="text-sm font-bold uppercase">Order #{selectedOrder.id.slice(0, 8)}</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between text-sm"><span>Status</span><span className="font-medium capitalize">{selectedOrder.status}</span></div>
                    <div className="flex justify-between text-sm"><span>Total</span><span className="font-medium">₵{selectedOrder.totalPrice.toLocaleString()}</span></div>
                    <div className="flex justify-between text-sm"><span>Date</span><span className="text-muted-foreground">{new Date(selectedOrder.createdAt).toLocaleDateString()}</span></div>
                    {selectedOrder.items?.length > 0 && (
                      <div className="border-t pt-2 mt-2">
                        <p className="text-xs font-medium mb-2">Items</p>
                        {selectedOrder.items.map((item: any, i: number) => (
                          <div key={i} className="text-sm flex justify-between py-1"><span>{item.productId}</span><span>x{item.quantity}</span></div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-sm font-bold uppercase">Delivery Tracking</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      {statusSteps.map((step, idx) => {
                        const currentIdx = getStatusIndex(selectedOrder.status);
                        const completed = idx <= currentIdx;
                        const Icon = step.icon;
                        return (
                          <div key={step.status} className={`flex flex-col items-center ${completed ? 'text-primary' : 'text-muted-foreground'}`}>
                            <Icon className="h-5 w-5 mb-1" />
                            <span className="text-[10px] uppercase">{step.label}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary transition-all" style={{ width: `${(getStatusIndex(selectedOrder.status) / 3) * 100}%` }} />
                    </div>
                    {shipment && (
                      <div className="bg-muted/50 p-3 rounded-md text-sm space-y-1">
                        <div className="font-medium flex items-center gap-2"><Truck className="h-4 w-4" /> {shipment.carrier}</div>
                        <div className="text-muted-foreground">Tracking: {shipment.trackingNumber}</div>
                        {shipment.estimatedDelivery && <div className="text-muted-foreground">ETA: {new Date(shipment.estimatedDelivery).toLocaleDateString()}</div>}
                        {shipment.events?.length > 0 && (
                          <div className="border-t pt-2 mt-2">
                            <p className="text-xs font-medium mb-1">History</p>
                            {shipment.events.slice().reverse().map((e: any, i: number) => (
                              <div key={i} className="text-xs text-muted-foreground flex justify-between">
                                <span>{e.status} - {e.location}</span>
                                <span>{new Date(e.timestamp).toLocaleDateString()}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="space-y-2">
                {orders.map((order) => (
                  <Card key={order.id} className="cursor-pointer hover:bg-muted/50" onClick={() => handleViewOrder(order)}>
                    <CardContent className="py-4 flex justify-between items-center">
                      <div>
                        <p className="font-medium text-sm">Order #{order.id.slice(0, 8)}</p>
                        <p className="text-xs text-muted-foreground">{new Date(order.createdAt).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">₵{order.totalPrice.toLocaleString()}</p>
                        <span className={`text-xs px-2 py-0.5 rounded ${order.status === 'delivered' ? 'bg-green-500/20 text-green-500' : order.status === 'shipped' ? 'bg-blue-500/20 text-blue-500' : order.status === 'paid' ? 'bg-yellow-500/20 text-yellow-500' : 'bg-muted text-muted-foreground'}`}>{order.status}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="security" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-bold uppercase">Two-Factor Authentication</CardTitle>
                <CardDescription>Add an extra layer of security to your account</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-muted/50 p-4 rounded-md space-y-3">
                  <div className="flex items-center gap-2">
                    <Key className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Authenticator App (TOTP)</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Use an authenticator app like Google Authenticator, Authy, or 1Password to generate verification codes.
                  </p>
                  <div className="space-y-2">
                    <Label className="text-xs">Enter your password to enable 2FA</Label>
                    <Input
                      type="password"
                      placeholder="Current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleEnable2FA} disabled={twoFactorLoading || !currentPassword}>
                      {twoFactorLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enable 2FA'}
                    </Button>
                  </div>
                </div>

                <div className="bg-muted/50 p-4 rounded-md space-y-3">
                  <span className="text-sm font-medium">Verify 2FA Code</span>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter 6-digit code"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value)}
                      maxLength={6}
                    />
                    <Button size="sm" onClick={handleVerify2FA} disabled={twoFactorLoading || twoFactorCode.length !== 6}>
                      Verify
                    </Button>
                  </div>
                </div>

                <div className="bg-destructive/10 p-4 rounded-md">
                  <span className="text-sm font-medium text-destructive">Disable 2FA</span>
                  <p className="text-xs text-muted-foreground mb-2">Turn off two-factor authentication</p>
                  <Button variant="destructive" size="sm" onClick={handleDisable2FA} disabled={twoFactorLoading || !currentPassword}>
                    Disable 2FA
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-bold uppercase">Change Password</CardTitle>
                <CardDescription>Update your account password</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs">Current Password</Label>
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">New Password</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <p className="text-[10px] text-muted-foreground">At least 8 characters</p>
                </div>
                <Button onClick={handleChangePassword} disabled={passwordLoading || !currentPassword || !newPassword}>
                  {passwordLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update Password'}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-bold uppercase">Session</CardTitle>
              </CardHeader>
              <CardContent>
                <Button variant="destructive" onClick={handleSignOut}>
                  <LogOut className="h-4 w-4 mr-2" /> Sign Out
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}